import { GoogleGenerativeAI } from '@google/generative-ai';
import { toast } from 'react-toastify';

/**
 * Modern AI Service integrating latest APIs:
 * - Gemini 2.5 Flash for text generation
 * - Imagen 4 for image generation  
 * - Enhanced audio with Indian languages
 */
class ModernAIService {
  constructor() {
    this.geminiClient = null;
    this.initializeServices();
  }

  initializeServices() {
    // Initialize Gemini 2.5 Flash
    const geminiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (geminiKey) {
      try {
        this.geminiClient = new GoogleGenerativeAI(geminiKey);
        console.log('✅ Gemini 2.5 Flash initialized');
      } catch (error) {
        console.error('❌ Gemini initialization failed:', error);
      }
    }

    // ElevenLabs TTS now handled by backend
    console.log('✅ TTS configured to use backend endpoint');
  }

  isGeminiAvailable() {
    return this.geminiClient !== null;
  }

  isElevenLabsAvailable() {
    // Check if backend TTS endpoint is available instead of frontend SDK
    return true; // Will be checked dynamically via backend health endpoint
  }

  /**
   * Generate images using Imagen 4 via Gemini API
   */
  async generateImage(prompt, options = {}) {
    try {
      toast.info('🎨 Generating image with Vidya-Agent Imagen...', { autoClose: 5000 });

      // Truncate long prompts to avoid token limits (100-150 words max)
      const truncatedPrompt = this.truncatePrompt(prompt, 120);
      
      // Use vidya-agent visual service for actual image generation
      const backendUrl = `${process.env.REACT_APP_VIDYA_AGENT_URL || 'http://localhost:3001'}/api`;
      
      const response = await fetch(`${backendUrl}/generate-visual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          concept: truncatedPrompt
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Vidya-Agent visual API error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(`Visual generation failed: ${data.message || 'Unknown error'}`);
      }

      // Map vidya-agent response to expected format
      return {
        success: true,
        data: {
          imageBase64: data.data.imageBase64,
          downloadUrl: data.data.downloadUrl || `data:${data.data.mimeType};base64,${data.data.imageBase64}`,
          filename: data.data.filename,
          mimeType: data.data.mimeType || 'image/jpeg',
          description: truncatedPrompt,
          source: data.data.source || 'Vidya-Agent Imagen',
          fileSize: data.data.fileSize
        }
      };

    } catch (error) {
      console.error('Vidya-Agent image generation failed:', error);
      throw new Error(`Image generation failed: ${error.message}. Please check backend connection and try again.`);
    }
  }


  /**
   * Truncate text for 30-second audio clips
   */
  truncateTextForAudio(text, maxDuration = 30) {
    // Approximate words per second for speech: 2.5-3 words per second
    // For 30 seconds: approximately 75-90 words
    const maxWords = Math.floor(maxDuration * 2.8); // Conservative estimate
    const words = text.trim().split(/\s+/);
    
    if (words.length <= maxWords) {
      return text;
    }
    
    const truncated = words.slice(0, maxWords).join(' ');
    return truncated + '...';
  }

  /**
   * Generate enhanced audio using ElevenLabs API with Indian language support (30-second clips)
   */
  async generateAudio(text, language = 'English', options = {}) {
    try {
      // Truncate text for 30-second audio clips
      const truncatedText = this.truncateTextForAudio(text, 30);
      
      // Generate enhanced script first using Gemini
      let enhancedScript = truncatedText;
      if (this.isGeminiAvailable()) {
        try {
          const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.5-flash' });
          const scriptResponse = await model.generateContent(`Enhance this text for natural 30-second audio narration in ${language}:
            
            "${truncatedText}"
            
            Make it:
            - Natural and conversational  
            - Properly paced for 30-second duration
            - Include natural pauses (use commas and periods)
            - Engaging for students
            - Maintain educational value
            - Keep within ~80 words for 30 seconds
            
            Return only the enhanced text, nothing else.`);
          
          enhancedScript = scriptResponse.response.text().trim();
          // Double-check the enhanced script doesn't exceed our limit
          enhancedScript = this.truncateTextForAudio(enhancedScript, 30);
        } catch (scriptError) {
          console.warn('Script enhancement failed, using truncated text:', scriptError);
        }
      }

      toast.info('🎵 Generating 30-second premium audio...', { autoClose: 5000 });
      
      // Use backend TTS endpoint instead of frontend SDK
      const backendUrl = `${process.env.REACT_APP_VIDYA_AGENT_URL || 'http://localhost:3001'}/api`;
      
      const response = await fetch(`${backendUrl}/tts/base64`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          text: enhancedScript,
          language: language,
          options: {
            stability: options.stability || 0.5,
            similarityBoost: options.similarity_boost || 0.8,
            style: options.style || 0.3
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Backend TTS error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(`Backend TTS failed: ${data.message || 'Unknown error'}`);
      }

      return {
        success: true,
        data: {
          originalTopic: text,
          generatedScript: enhancedScript,
          audioBase64: data.data.audioBase64,
          downloadUrl: data.data.downloadUrl,
          filename: data.data.filename,
          mimeType: data.data.mimeType,
          fileSize: data.data.fileSize,
          duration: Math.min(30, Math.ceil(enhancedScript.length / 12)), // Max 30 seconds
          language: data.data.language,
          voice: data.data.voice,
          source: data.data.source + ' - 30sec',
          hasAudioControls: true, // Flag for audio controls
          truncated: truncatedText.length < text.length
        }
      };

    } catch (error) {
      console.error('Backend TTS audio generation failed:', error);
      throw new Error(`Backend TTS audio generation failed: ${error.message}. Please check backend connection and try again.`);
    }
  }

  /**
   * Get ElevenLabs voice configuration for different languages
   */
  getElevenLabsVoice(language) {
    const voices = {
      'English': { 
        voiceId: 'pNInz6obpgDQGcFmaJgB', // Adam - natural male voice
        name: 'Adam (English)'
      },
      'Hindi': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9', // Multilingual female
        name: 'Aria (Hindi)'
      },
      'Marathi': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9', // Same multilingual voice
        name: 'Aria (Marathi)'
      },
      'Gujarati': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9',
        name: 'Aria (Gujarati)'
      },
      'Bengali': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9',
        name: 'Aria (Bengali)'
      },
      'Tamil': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9',
        name: 'Aria (Tamil)'
      },
      'Telugu': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9',
        name: 'Aria (Telugu)'
      }
    };

    return voices[language] || voices['English'];
  }


  /**
   * Generate text content using Gemini 2.5 Flash with structured response
   */
  async generateContent(concept, gradeLevel, language = 'English') {
    if (!this.isGeminiAvailable()) {
      throw new Error('Gemini API not available. Set REACT_APP_GEMINI_API_KEY');
    }

    try {
      toast.info('📝 Generating structured content with Gemini 2.5 Flash...', { autoClose: 3000 });

      // Truncate long concepts to avoid token limits (100-150 words max)
      const truncatedConcept = this.truncatePrompt(concept, 120);

      const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      const structuredPrompt = `Create educational content about "${truncatedConcept}" for Grade ${gradeLevel} students in ${language}.

CRITICAL: You must return ONLY valid JSON with no additional text, markdown formatting, or explanations.

Return this exact JSON structure:
{
  "title": "Engaging title for the topic",
  "introduction": "Brief introduction paragraph (2-3 sentences)",
  "mainContent": "Main educational content (200-300 words)",
  "keyPoints": ["Key point 1", "Key point 2", "Key point 3"],
  "funFact": "Interesting fun fact about the topic",
  "conclusion": "Concluding paragraph (2-3 sentences)",
  "vocabulary": ["Important term 1", "Important term 2", "Important term 3"]
}

Requirements:
- Age-appropriate for Grade ${gradeLevel}
- Clear, engaging explanation in ${language}
- Use simple language suitable for the grade level
- Include practical examples
- Make it educational and interesting
- Return ONLY valid JSON - no markdown, no explanations, no extra text`;

      const response = await model.generateContent(structuredPrompt);
      let contentText = response.response.text().trim();

      // More aggressive cleanup for better JSON parsing
      contentText = contentText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      contentText = contentText.replace(/^[^{]*{/, '{'); // Remove text before first {
      contentText = contentText.replace(/}[^}]*$/, '}'); // Remove text after last }
      
      let parsedContent;
      try {
        parsedContent = JSON.parse(contentText);
        
        // Validate required fields
        const requiredFields = ['title', 'introduction', 'mainContent', 'keyPoints', 'funFact', 'conclusion', 'vocabulary'];
        const missingFields = requiredFields.filter(field => !parsedContent[field]);
        
        if (missingFields.length > 0) {
          console.warn(`Missing required fields: ${missingFields.join(', ')}, using fallback`);
          throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
        }
        
      } catch (jsonError) {
        console.warn('JSON parsing failed, creating structured fallback:', jsonError);
        console.warn('Raw content:', contentText);
        
        // Create a structured fallback from the raw text
        const cleanText = contentText.replace(/[{}"'\[\]]/g, '').trim();
        parsedContent = {
          title: `Learning About ${truncatedConcept}`,
          introduction: `Let's explore the fascinating topic of ${truncatedConcept} in a way that's perfect for Grade ${gradeLevel} students.`,
          mainContent: cleanText.length > 50 ? cleanText.substring(0, 400) + '...' : `${truncatedConcept} is an important topic that helps us understand many aspects of our world. Through this lesson, students will discover key concepts, explore practical examples, and develop a deeper appreciation for this subject.`,
          keyPoints: [
            `Understanding ${truncatedConcept}`,
            "Key characteristics and features",
            "Real-world applications"
          ],
          funFact: `Did you know? ${truncatedConcept} has many interesting aspects that make learning fun!`,
          conclusion: `Now you have a better understanding of ${truncatedConcept}. Keep exploring and learning!`,
          vocabulary: [truncatedConcept, "Learning", "Education"]
        };
      }

      // Ensure all required fields exist with fallbacks
      const structuredData = {
        title: parsedContent.title || `Learning About ${truncatedConcept}`,
        introduction: parsedContent.introduction || `Let's learn about ${truncatedConcept}!`,
        mainContent: parsedContent.mainContent || contentText,
        keyPoints: Array.isArray(parsedContent.keyPoints) ? parsedContent.keyPoints : [`Understanding ${truncatedConcept}`, "Key features", "Applications"],
        funFact: parsedContent.funFact || `${truncatedConcept} is an interesting topic to explore!`,
        conclusion: parsedContent.conclusion || `Great job learning about ${truncatedConcept}!`,
        vocabulary: Array.isArray(parsedContent.vocabulary) ? parsedContent.vocabulary : [truncatedConcept, "Learning", "Knowledge"]
      };

      // Combine all content into a story format for backward compatibility
      const combinedStory = `${structuredData.title}\n\n${structuredData.introduction}\n\n${structuredData.mainContent}\n\nKey Points:\n${structuredData.keyPoints.map(point => '• ' + point).join('\n')}\n\nFun Fact: ${structuredData.funFact}\n\n${structuredData.conclusion}`;
      
      return {
        success: true,
        data: {
          story: combinedStory, // Main content for backward compatibility
          ...structuredData,
          model: 'gemini-2.5-flash',
          generationTime: '2-3 seconds',
          language: language,
          gradeLevel: gradeLevel,
          concept: truncatedConcept,
          source: 'Gemini 2.5 Flash Structured',
          format: 'structured_educational_content'
        }
      };

    } catch (error) {
      console.error('Content generation failed:', error);
      
      // Return a structured fallback response
      const fallbackData = {
        title: `Learning About ${concept}`,
        introduction: `Welcome to learning about ${concept}! This topic is designed for Grade ${gradeLevel} students.`,
        mainContent: `${concept} is an important topic that helps us understand many aspects of our world. Through this lesson, students will discover key concepts, explore practical examples, and develop a deeper appreciation for this subject. The content is carefully crafted to be engaging and appropriate for Grade ${gradeLevel} level learning.`,
        keyPoints: [
          `Introduction to ${concept}`,
          "Key characteristics and features", 
          "Real-world applications and examples"
        ],
        funFact: `Did you know that ${concept} connects to many other subjects and areas of learning?`,
        conclusion: `Great work exploring ${concept}! Continue to ask questions and discover more about this fascinating topic.`,
        vocabulary: [concept, "Learning", "Education", "Knowledge"]
      };
      
      const combinedStory = `${fallbackData.title}\n\n${fallbackData.introduction}\n\n${fallbackData.mainContent}\n\nKey Points:\n${fallbackData.keyPoints.map(point => '• ' + point).join('\n')}\n\nFun Fact: ${fallbackData.funFact}\n\n${fallbackData.conclusion}`;
      
      return {
        success: true,
        data: {
          story: combinedStory, // Main content for backward compatibility
          ...fallbackData,
          model: 'gemini-2.5-flash-fallback',
          generationTime: '1 second',
          language: language,
          gradeLevel: gradeLevel,
          concept: concept,
          source: 'Structured Fallback Generator',
          format: 'structured_educational_content',
          note: 'Generated using fallback due to API issues'
        }
      };
    }
  }

  /**
   * Generate worksheets using Gemini 2.5 Flash
   */
  async generateWorksheet(concept, grades, language = 'English') {
    try {
      toast.info('📋 Generating worksheets with Vidya-Agent...', { autoClose: 5000 });

      // Truncate long concepts to avoid token limits (100-150 words max)
      const truncatedConcept = this.truncatePrompt(concept, 100);

      // Use vidya-agent worksheet service
      const backendUrl = `${process.env.REACT_APP_VIDYA_AGENT_URL || 'http://localhost:3001'}/api`;
      
      const response = await fetch(`${backendUrl}/generate-worksheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          concept: truncatedConcept,
          grades: grades,
          language: language
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Vidya-Agent worksheet API error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'success') {
        throw new Error(`Worksheet generation failed: ${data.message || 'Unknown error'}`);
      }

      return {
        success: true,
        data: {
          topic: truncatedConcept,
          worksheets: data.data.worksheets || [],
          totalGenerated: data.data.worksheets?.length || 0,
          language: language,
          grades: grades,
          source: data.data.source || 'Vidya-Agent Worksheets'
        }
      };

    } catch (error) {
      console.error('Vidya-Agent worksheet generation failed:', error);
      throw new Error(`Worksheet generation failed: ${error.message}. Please check backend connection and try again.`);
    }
  }

  /**
   * Generate multi-language audio for Indian regional languages
   */
  async generateMultiLanguageAudio(text, languages = ['English', 'Hindi', 'Marathi', 'Gujarati']) {
    toast.info('🌐 Generating multi-language audio...', { autoClose: 5000 });

    const audioResults = [];
    const errors = [];

    for (const language of languages) {
      try {
        const result = await this.generateAudio(text, language);
        audioResults.push({
          language: language,
          ...result.data
        });
      } catch (error) {
        console.warn(`Audio generation failed for ${language}:`, error);
        errors.push({ language, error: error.message });
      }
    }

    return {
      success: audioResults.length > 0,
      data: {
        originalText: text,
        languages: languages,
        audioFiles: audioResults,
        errors: errors,
        totalGenerated: audioResults.length,
        totalRequested: languages.length,
        source: 'Multi-Language Audio Generator'
      }
    };
  }


  /**
   * Get ElevenLabs voice configuration for different languages
   */
  getElevenLabsVoice(language) {
    const voices = {
      'English': { 
        voiceId: 'pNInz6obpgDQGcFmaJgB', // Adam - natural male voice
        name: 'Adam (English)'
      },
      'Hindi': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9', // Multilingual female
        name: 'Aria (Hindi)'
      },
      'Marathi': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9', // Same multilingual voice
        name: 'Aria (Marathi)'
      },
      'Gujarati': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9',
        name: 'Aria (Gujarati)'
      },
      'Bengali': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9',
        name: 'Aria (Bengali)'
      },
      'Tamil': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9',
        name: 'Aria (Tamil)'
      },
      'Telugu': { 
        voiceId: 'cgSgspJ2msm6clMCkdW9',
        name: 'Aria (Telugu)'
      }
    };

    return voices[language] || voices['English'];
  }


  /**
   * Convert blob to base64
   */
  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Health check for all services
   */
  getServiceStatus() {
    return {
      gemini: {
        available: this.isGeminiAvailable(),
        model: 'gemini-2.5-flash',
        features: ['text-generation', 'image-generation', 'worksheet-generation']
      },
      elevenLabs: {
        available: this.isElevenLabsAvailable(),
        features: ['backend-tts', 'indian-languages', 'natural-voices']
      }
    };
  }

  /**
   * Truncate long prompts to avoid token limits
   */
  truncatePrompt(prompt, maxWords = 100) {
    if (!prompt) return '';
    
    const words = prompt.trim().split(/\s+/);
    if (words.length <= maxWords) {
      return prompt;
    }
    
    const truncated = words.slice(0, maxWords).join(' ');
    return truncated + '...';
  }
}

export default ModernAIService;